import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Typography,
} from "@mui/material";
import ActivityReviewComment from "./ActivityReviewComment";
import FormControl from "@mui/material/FormControl";

function ActivityReviewFormDialog({
    open,
    formTitle,
    comments,
    setComments,
    status,
    setStatus,
    onClose,
    onSubmit,
    canUpdate,
    disableSubmit,
}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="lg">
            <DialogTitle>{formTitle}</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    {canUpdate ? (
                        <>
                            <Box className={"form-row"}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="review_na"
                                                disabled={!canUpdate}
                                                checked={status === "n/a"}
                                                onChange={({ target }) =>
                                                    setStatus(
                                                        target.checked
                                                            ? "n/a"
                                                            : null
                                                    )
                                                }
                                            />
                                        }
                                        label="Reflection not applicable"
                                    />
                                </FormGroup>
                            </Box>

                            {!!comments &&
                                comments
                                    .sort((a, b) => a.index - b.index)
                                    .map((comment, index) => (
                                        <Box className={"form-row"} key={index}>
                                            <Typography
                                                component="h3"
                                                variant="h3"
                                            >
                                                {comment?.title ||
                                                    comment?.label}
                                            </Typography>
                                            <Typography component="p" mb={2}>
                                                {comment?.description}
                                            </Typography>
                                            <FormControl
                                                component="div"
                                                fullWidth={true}
                                                required={
                                                    !!comment.isRequired &&
                                                    status !== "n/a"
                                                }
                                                disabled={status === "n/a"}
                                                error={
                                                    comment?.isRequired &&
                                                    !comment?.response &&
                                                    status !== "n/a"
                                                }
                                            >
                                                <InputLabel
                                                    sx={{
                                                        backgroundColor: "#FFF",
                                                        padding: "0 4px",
                                                    }}
                                                >
                                                    {comment?.isRequired
                                                        ? "Required"
                                                        : "Add comment"}
                                                </InputLabel>
                                                <OutlinedInput
                                                    id={`text-review_${index}`}
                                                    value={comment?.response}
                                                    onChange={({ target }) =>
                                                        setComments([
                                                            ...comments.filter(
                                                                ({ name }) =>
                                                                    name !==
                                                                    comment.name
                                                            ),
                                                            {
                                                                ...comments.find(
                                                                    ({
                                                                        name,
                                                                    }) =>
                                                                        name ===
                                                                        comment.name
                                                                ),
                                                                response:
                                                                    target.value,
                                                            },
                                                        ])
                                                    }
                                                    multiline
                                                    rows={4}
                                                />
                                                <FormHelperText>
                                                    {comment.helperText}
                                                </FormHelperText>
                                            </FormControl>
                                        </Box>
                                    ))}
                        </>
                    ) : (
                        <>
                            {!!comments &&
                                comments
                                    .sort((a, b) => a.index - b.index)
                                    .map((comment, index) => (
                                        <ActivityReviewComment
                                            key={index}
                                            comment={comment}
                                        />
                                    ))}
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>
                {canUpdate && (
                    <Button
                        key="save"
                        onClick={onSubmit}
                        variant="contained"
                        color="primary"
                        disableElevation={true}
                        disabled={disableSubmit}
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

function ActivityReviewFormDialogContainer({
    formTitle,
    onSave,
    onClose,
    data = {},
    canUpdate,
    ...props
}) {
    const [comments, setComments] = useState(
        !data?.review?.comments
            ? []
            : data.review.comments.map((comment, index) => ({
                  ...comment,
                  index,
              }))
    );
    const [status, setStatus] = useState(data.review?.status || null);

    const checkNullResponse = (comments) => {
        for (let i = 0; i < comments.length; i++) {
            if (
                comments[i]?.isRequired &&
                (comments[i]?.response == null ||
                    comments[i]?.response?.length < 1)
            ) {
                return true;
            }
        }
        return false;
    };

    const [hasNullResponse, setHasNullResponse] = useState(
        checkNullResponse(comments)
    );

    const saveComments = (newComments) => {
        setHasNullResponse(checkNullResponse(newComments));
        setComments(newComments);
    };

    const onSubmit = () => {
        const params = {
            ...data.review,
            comments,
            status: status === "n/a" ? "n/a" : "completed",
        };
        onSave(params);
        onClose();
    };

    const disableSubmit = hasNullResponse && status !== "n/a";

    return (
        <ActivityReviewFormDialog
            formTitle={formTitle}
            onClose={onClose}
            canUpdate={canUpdate}
            onSubmit={onSubmit}
            comments={comments}
            setComments={saveComments}
            status={status}
            setStatus={setStatus}
            disableSubmit={disableSubmit}
            {...props}
        />
    );
}

export default ActivityReviewFormDialogContainer;
